import * as Sentry from "@sentry/vue";
import {
  type Awaitable,
  type MaybeRefOrGetter,
  tryOnMounted,
  tryOnUnmounted,
  useElementSize,
  useElementVisibility,
  useScroll,
} from "@vueuse/core";
import { computed, type ComputedRef, nextTick, reactive, type Ref, ref, toValue, watch } from "vue";

type ScrollContainer = HTMLElement | Window | Document | null | undefined;
type ScrollElement = HTMLElement | null | undefined;

const LOAD_MORE_DISTANCE_PX = 500;
const DEBOUNCE_TIME_MS = 500;
const STALE_TIME_MS = 1000 * 60 * 5;

const keyCacheMap = reactive(
  new Map<string, { lastLoadTime: number; totalCount: number | null; activePromise?: Promise<number> }>()
);

// Document and Window cannot be observed by IntersectionObserver
const resolveElement = (el: ScrollContainer) => {
  if (typeof Window !== "undefined" && el instanceof Window) {
    return el.document.documentElement;
  }
  if (typeof Document !== "undefined" && el instanceof Document) {
    return el.documentElement;
  }
  return el as ScrollElement;
};

const usePaginatedList = <T extends ScrollContainer>(
  key: Ref<string>,
  scrollContainer: MaybeRefOrGetter<T>,
  alreadyLoadedCount: ComputedRef<number>,
  loadMore: (loadedCount: number) => Awaitable<number>,
  // The amount of spacing after the last item, default matches extra-overscroll
  loadMoreOffset: MaybeRefOrGetter<number> = 300,
  options: {
    direction?: "top" | "bottom" | "left" | "right";
    ignoreContainerResize?: boolean;
  } = {}
) => {
  const { direction = "bottom", ignoreContainerResize = false } = options;
  const state = reactive(
    useScroll(scrollContainer, {
      offset: { [direction]: toValue(loadMoreOffset) + LOAD_MORE_DISTANCE_PX },
    })
  );
  const keyValue = computed(() => toValue(key));
  const totalCount = ref<number | null>(keyCacheMap.get(keyValue.value)?.totalCount ?? null);

  const isLoading = computed(
    () =>
      !!keyCacheMap.get(keyValue.value)?.activePromise || (totalCount.value === null && alreadyLoadedCount.value === 0)
  );
  const canLoadMore = computed(() => totalCount.value === null || alreadyLoadedCount.value < totalCount.value);

  const observedElement = computed<ScrollElement>(() => resolveElement(toValue(scrollContainer)));
  const isElementVisible = useElementVisibility(observedElement);
  const elementSize = useElementSize(observedElement);

  const lastLoadTime = ref<number>(keyCacheMap.get(keyValue.value)?.lastLoadTime ?? 0);
  const checkAndLoad = async (force: boolean = false, ignoreStale: boolean = false) => {
    state.measure();

    const cacheEntry = keyCacheMap.get(keyValue.value);

    if (
      cacheEntry?.activePromise ||
      !canLoadMore.value ||
      (!force && (!observedElement.value || !isElementVisible.value))
    ) {
      return;
    }

    let isWithinContainer = false;
    if (observedElement.value) {
      const { scrollHeight, clientHeight, scrollWidth, clientWidth } = observedElement.value;
      isWithinContainer =
        direction === "bottom" || direction === "top" ? scrollHeight <= clientHeight : scrollWidth <= clientWidth;
    }

    if (state.arrivedState[direction] || isWithinContainer || force) {
      // Debounce the load to prevent rapid successive loads
      const now = Date.now();
      if (!force && now - lastLoadTime.value < DEBOUNCE_TIME_MS) {
        return;
      }

      // Check if we're within stale time for forced loads
      const keyLastLoadTime = cacheEntry?.lastLoadTime;
      if (force && ignoreStale && keyLastLoadTime && now - keyLastLoadTime < STALE_TIME_MS) {
        return;
      }

      const previousTotalCount = totalCount.value;

      try {
        lastLoadTime.value = now;

        const activePromise = Promise.resolve(loadMore(alreadyLoadedCount.value));
        keyCacheMap.set(keyValue.value, {
          lastLoadTime: now,
          totalCount: cacheEntry?.totalCount ?? totalCount.value,
          activePromise,
        });
        const result = await activePromise;

        totalCount.value = result;
        const cacheEntryToUpdate = keyCacheMap.get(keyValue.value);
        if (cacheEntryToUpdate) {
          cacheEntryToUpdate.totalCount = result;
          delete cacheEntryToUpdate.activePromise;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        totalCount.value = totalCount.value ?? alreadyLoadedCount.value;
      } finally {
        const currentCacheEntry = keyCacheMap.get(keyValue.value);
        if (currentCacheEntry?.activePromise) {
          delete currentCacheEntry.activePromise;
        }

        // Load more as needed, which happens when the current batch doesn't fill the element
        if (totalCount.value !== previousTotalCount) {
          nextTick(() => checkAndLoad());
        } else {
          Sentry.captureMessage("Attempted to load more items but total count didn't change");
        }
      }
    }
  };

  const forceCheckAndLoad = () => checkAndLoad(true, true);

  // Reset the state
  const reset = () => {
    keyCacheMap.delete(keyValue.value);
    totalCount.value = null;
    lastLoadTime.value = 0;
    checkAndLoad(true);
  };

  // Initial load
  tryOnMounted(() => forceCheckAndLoad());

  // Watch for changes
  const stop = watch(
    // elementSize.width.value needs to be added here if we implement horizontal infinite scroll
    // but right now it causes issues with vertical infinite scroll when the vertical scrollbar appears/disappears
    [
      () => state.arrivedState[direction],
      isElementVisible,
      () => window.innerHeight,
      () => (ignoreContainerResize ? undefined : elementSize.height.value),
    ],
    () => checkAndLoad()
  );
  tryOnUnmounted(stop);

  return { isLoading, totalCount, canLoadMore, reset, loadMore: forceCheckAndLoad };
};

export default usePaginatedList;
